export type FAQItem = {
  q: string;
  a: string;
};
export const FAQ_TOPICS = [
  'Account',
  'Listing',
  'Purchases',
  'Cancellations & Refunds',
  'Shipping',
  'Returns',
  'Selling',
  'Payouts',
  'Trust & Safety',
] as const;
export type FAQTopic = (typeof FAQ_TOPICS)[number];
export const ACCOUNT_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I change my username?',
    a: `
      
        You may change your username by going to account overview, and visiting
        the edit profile page. Please note, you can only change your username
        once every 30 days.
      
    `,
  },
  {
    q: 'How do I change my email?',
    a: `Visit your ‘Account Overview’ and click the ‘edit’ button.`,
  },
  {
    q: 'Why was my account suspended?',
    a: `
      
        If you have violated our Terms & Conditions or are under suspicion of
        using MX Locker to sell or buy outside of the platform, your account
        will be suspended. If you believe your account was suspended and want us
        to review your account, please contact us at support@mxlocker.com
      
    `,
  },
  {
    q: 'Why was I charged a fee?',
    a: `
      
        To help MX Locker run smoothly and to cover the costs of the products
        and services we provide, we charge a service fee on our platform. You
        may review the breakdown of fees by visiting our Fee Policy page.
      
    `,
  },
  {
    q: 'Someone left a false review, can it be removed?',
    a: `
      
        If a Buyer leaves a false or inaccurate review, please report it to our
        support team and it will be investigated. If it’s determined to be false
        or misleading, it will be removed from your account.
      
    `,
  },
  {
    q: 'How do I change my address?',
    a: `
      
        You can edit all saved addresses under the “Addresses” tab under Account
        Overview.
      
    `,
  },
];
export const LISTING_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I properly photograph my item when listing it?',
    a: `
      
        We suggest taking photos of the front, back and sides of the item to
        ensure the full condition is shown. We recommend taking photos against a
        clear background and in good lighting to ensure the best exposure for
        your listings to sell.
      
    `,
  },
  {
    q: 'What is required when describing my item?',
    a: `
      
        An in-depth and detailed description of the item is suggested when
        listing. Ensuring the buyer of what they are receiving is a key element
        to a satisfied customer and successful sale. If there is a part number
        available, we also recommend putting this in the description.
      
    `,
  },
  {
    q: 'How do I price my item correctly?',
    a: `
      
        We recommend researching the exact item you are listing to ensure your
        pricing is fair for the item provided. You may filter by “sold” on MX
        Locker to see what your item is selling for.
      
    `,
  },
  {
    q: 'I sold my item elsewhere and didn’t remove my listing before the item sold on MX Locker. What should I do?',
    a: `
      
        In order to ensure the integrity of our marketplace, and build trust
        amongst buyers and sellers on MX Locker, we enforce strict seller
        obligations amongst our sellers. If you sell through MX Locker, you are
        responsible for removing items you are no longer selling, and failing to
        do so will result in our seller cancellation fee.
      
    `,
  },
  {
    q: 'What are some things that cannot be sold on MX Locker?',
    a: `
      
        MX Locker is a marketplace dedicated to powersports and cycling. This includes dirt bikes, ATVs, UTVs, motorcycles, mountain bikes, and related gear, parts, and accessories. Items outside of these categories—such as home goods, electronics, fashion, or automotive parts not intended for powersports or mountain biking—are not allowed on MX Locker. All listings must be relevant to motocross, off-road, street powersports, or cycling.
      
    `,
  },
];
export const PURCHASE_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I view my recent purchase?',
    a: `
      
        You are able to view all of your recent purchases via the “Purchases”
        tab on your MX Locker account.
      
    `,
  },
  {
    q: 'How long does a Seller have to ship my order?',
    a: `
      
        We ask sellers to ship within 3 business days. If you are experiencing a
        delay, please reach out to your seller directly or ask us to step in. If
        the item is not shipped within 5 business days, you are eligible to request a cancellation at no charge through your Purchases page.
      
    `,
  },
  {
    q: 'What happens if my item never ships?',
    a: `
      
        If your order is not processed within the time period allowed, you will
        be offered the opportunity to upgrade your item to another of similar
        value. If you do not wish to upgrade your item, your order will be fully
        refunded.
      
    `,
  },
  {
    q: 'Is trading allowed on MX Locker?',
    a: `
      
        If a user asks you to trade on MX Locker, we do not recommend it. MX
        Locker is not responsible for protecting such transactions. You may not
        receive what was promised during the trade, and in such cases, we cannot
        offer refunds or returns. Our ability to keep the marketplace safe for
        all users depends on the adherence of all users to our policies. We
        recommend keeping all transactions within MX Locker.
      
    `,
  },
  {
    q: 'Can I meet up with my Seller in person to receive my order?',
    a: `
      
        After the transaction occurs through the platform, we will allow both
        parties to meet in person to exchange the item if mutually arranged. If
        you are meeting in person, please contact MX Locker support to provide
        proof that the item was delivered to the rightful purchaser and funds
        can be released to the seller.
      
    `,
  },
  {
    q: 'How do I avoid being scammed?',
    a: `
      
        Double-check deals that seem too good to be true, don’t give out
        personal information, review the Seller’s rating on their profile. If a
        user attempts to negotiate outside of MX Locker, these deals usually
        lead to fraud and scams, and are not protected by MX Locker.
      
    `,
  },
  {
    q: 'Can I cancel or modify my order after I have placed it?',
    a: `
      
        If you need to cancel or modify your order, please contact MX Locker
        customer service as soon as possible. Changes or cancellations may not
        be possible if the order has already been processed for shipping.
      
    `,
  },
  {
    q: 'Does MX Locker offer international shipping?',
    a: `
      
        Yes, however, international shipping is determined by the sellers on our
        platform. Each seller sets their own shipping rates and determines which
        countries they are willing to ship to.
      
    `,
  },
  {
    q: 'What if an item is out of stock?',
    a: `
      
        If an item is out of stock, you may be able to sign up for a
        notification when it becomes available again. MX Locker may also offer
        alternative products that are similar to the item you wanted to
        purchase.
      
    `,
  },
];
export const CANCELLATION_REFUND_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'Who is responsible for shipping a return?',
    a: `The Buyer will be responsible for returning the item.`,
  },
  {
    q: 'What is eligible for a return?',
    a: `
      
        If the item arrives damaged, defective or is different than advertised
        in the listing. If you decide you do not like/need the item after
        purchasing, we unfortunately can not accept your return request. You can
        always re-list the item on MX Locker!
      
    `,
  },
  {
    q: 'Can I exchange my item for a different size?',
    a: `
      
        This is up to the Seller. We always suggest communicating with the
        Seller before purchasing the item or viewing the Seller’s return
        policies.
      
    `,
  },
  {
    q: 'How long do I have to report an issue with my item?',
    a: `
      
        We allow a 48 hour window after the item has been delivered to report
        any issues with your order. After 48 hours, the funds are released to
        the Seller and the order is marked as “Complete”.
      
    `,
  },
  {
    q: 'How do I cancel my order?',
    a: `
      
        Please contact our support team with your order cancellation request. If
        your request falls within our cancellation policy, it will be processed
        and funds will be returned back to the original payment method.
      
    `,
  },
  {
    q: 'How do I retract an offer?',
    a: `
      
        Once submitted, all offers are binding and cannot be canceled. You must
        acknowledge and agree to these terms before submitting your offer.
        Offers automatically expire after 24 hours.
      
    `,
  },
  {
    q: 'What is the cancellation policy?',
    a: `
      
        We can cancel the item if you mistakenly purchased the incorrect item, a
        duplicate or would like to select a different item/changed your mind. If
        we suspect both parties are attempting to take the transaction outside
        of the platform, both accounts will be suspended and may be responsible
        for a cancellation fee.
      
    `,
  },
  {
    q: 'How can I partially refund my Buyer?',
    a: `
      
        Our support team will partially refund the Buyer upon mutual request
        from both parties. The refund will be deducted from your MX Locker
        balance and placed back on the Buyer’s original payment method.
      
    `,
  },
  {
    q: 'How long does it take to receive my refund?',
    a: `
      
        Depending on when the refund was initiated, it may take between 5-10
        business days for funds to arrive. If funds are requested to go back to
        your MX Locker balance, it will typically be available within 48 hours
        of the request.
      
    `,
  },
];
export const SHIPPING_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I package my item correctly?',
    a: `
      
        Make sure to protect the item (bubble wrap or packing paper is most
        common) and limit the item from moving around inside of the box as best
        as possible. Once the item has been packed properly, you will need the
        weight and dimensions of the package. Double check the outside of the
        box and remove/cover any previous labels that were used (if any).
      
    `,
  },
  {
    q: 'How do I update tracking information on my order?',
    a: `
      
        You are able to input the tracking number to your item via the order
        page.
      
    `,
  },
  {
    q: 'What should I do if my item gets lost in transit?',
    a: `
      
        We suggest contacting the shipping provider used in regards to damaged
        or lost packages. If you need any assistance in helping locate a lost
        package, or best practices, please contact us at MX Locker support.
      
    `,
  },
  {
    q: 'My item was returned to me due to insufficient address. What should I do?',
    a: `
      
        Please contact our support team in regards to this. We will place the
        order on hold until a sufficient address is supplied from the Buyer. If
        no response is received, a refund will be issued to the Buyer and we
        will re-list your item if requested.
      
    `,
  },
  {
    q: 'How do I print a shipping label for my order?',
    a: `
      
        Once you receive an order, you will be able to print the label directly
        from the order page. You will be prompted to fill in the package
        information (weight + dimensions) and will be able to choose the desired
        shipping service. If you need assistance, please reach out to our
        support team - they will be happy to help!
      
    `,
  },
  {
    q: 'Where does MX Locker allow shipping to?',
    a: `
      
        Worldwide shipping is available with MX Locker. As a Seller, you are
        able to select which countries/continents you’re willing to ship your
        item to.
      
    `,
  },
  {
    q: 'I shipped my order through my local USPS/UPS/FedEx. Why do I need the tracking number?',
    a: `
      
        The tracking number is required to update your order status. MX Locker
        will automatically update your order status every step of the way, and
        allows both the Buyer and Seller to keep track of the item. Funds for
        your order will not be released until the item is marked as ‘Delivered’,
        which is only available via a tracking number or confirmation from the
        Buyer.
      
    `,
  },
  {
    q: 'I lost my tracking number. What should I do?',
    a: `
      
        Please contact our support team & communicate with the Buyer to inform
        them that the item has been shipped. Our support team will follow up
        with the Buyer and confirm delivery of the item before changing the
        order status and releasing funds from the order.
      
    `,
  },
  {
    q: 'My buyer claims they never received the item. How do I double check this?',
    a: `
      
        If your buyer claims that an item you sent hasn’t arrived, please check
        the tracking information for the shipment and contact the carrier to
        locate the package if necessary. If your tracking information shows your
        package was delivered but it’s missing, ask the buyer to:
        \n
        - Verify the shipping address on your order confirmation.
        \n
        - Look for a notice of attempted delivery. Look around the delivery
        location for your package.
        \n
        - Check to see if someone else (family member, roommates, neighbors)
        accepted the delivery
        \n- Wait 48 hours. In some cases packages are delivered while they
        are still in transit.
      
    `,
  },
];
export const RETURNS_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How long do I have to return my item?',
    a: `
      
        We allow 48 hours after the item is ‘Delivered’ to report any issues
        with your order. If the wrong item arrives, the item is incorrectly
        described or does not work, you are able to return for a full refund. If
        the correct item arrives and simply does not fit, we will not accept a
        return for a refund - only a return for a size exchange.

        Once a return label is issued by MX Locker support, 
        you have 48 hours to place your order in transit back to the seller.
        Failure to return your item in the 48-hour time period will result in funds 
        automatically being released to the seller, and the return canceled.
      
    `,
  },
  {
    q: 'Does MX Locker provide a return label for me?',
    a: `
        Yes MX Locker will provide a return label. The cost of the return label is either charged to the buyer or the seller depending on who is at fault.
    `,
  },
  {
    q: 'My item has been returned to me. How do I refund the Buyer?',
    a: `
      
        We suggest contacting the shipping provider used in regards to damaged
        or lost packages. If you need any assistance in helping locate a lost
        package, or best practices, please contact us at MX Locker support.
      
    `,
  },
  {
    q: 'My item was returned to me due to insufficient address. What should I do?',
    a: `
      
        Please contact our support team regarding refunds. Our team will
        overlook all refunds to ensure both the Buyer and Seller’s situations
        are resolved before proceeding to refund.
      
    `,
  },
  {
    q: 'Can I exchange a product for a different one?',
    a: `
      
        MX Locker does not currently offer exchanges. If you would like a
        different product, you can relist your item on MX Locker and place a new
        order!
      
    `,
  },
  {
    q: 'Can I return a product if I have used it?',
    a: `
      
        MX Locker will not accept returns for products that have been used or
        damaged by the customer.
      
    `,
  },
  {
    q: 'Who pays for return shipping?',
    a: `
      
        If the product is damaged or defective, MX Locker will provide a prepaid
        shipping label for the return.
      
    `,
  },
  {
    q: 'How long does it take to process a return?',
    a: `
      
        Once MX Locker receives your returned item, it may take up to 7 business
        days to process the return and issue a refund.
      
    `,
  },
];
export const SELLING_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I list my item correctly?',
    a: `
      
        Make sure to get pictures of the item from the front, back, top, bottom
        and sides along with any defects or markings the item may have. A full
        description of the item & condition is highly recommended for the best
        selling experience.
      
    `,
  },
  {
    q: 'Where can I view my recent order(s) at? ',
    a: `
      
        You can view all orders in the Orders tab under your profile. If you are
        on the app, please navigate to your profile, where you will find the
        selling tab.
      
    `,
  },
  {
    q: 'How long do I have to ship my order?',
    a: `
      
        We allow up to 3 business days for orders to be shipped. If your order
        is not shipped within 3 business days, you may be subject to late fees
        and multiple violations will result in account deactivation. If you need
        more time to ship, please notify the MX Locker support team.
      
    `,
  },
  {
    q: 'How do I avoid late fees and penalties if I am out of town/on vacation?',
    a: `
      
        Please enable ‘Vacation Mode’ to draft all active listings and prevent
        any late fees or penalties.
      
    `,
  },
  {
    q: ' I don’t feel safe sending my item out without receiving payment. How can I ensure that I will receive my funds?',
    a: `
      
        Delaying the availability of funds is a common practice in online
        marketplaces. We implement this to help ensure that MX Locker is safe
        and secure for both buyers and sellers. All funds are held in Escrow and
        are not released until all proper requirements are met. You are always
        fully protected when buying & selling with MX Locker.
      
    `,
  },
  {
    q: 'What is a selling policy?',
    a: `
      
        A selling policy is a set of rules and guidelines that govern how a
        seller can sell products or services to consumers.
      
    `,
  },
  {
    q: 'What are the common elements of a selling policy?',
    a: `
      
        Common elements of a selling policy include payment terms, shipping
        options, return policies, product descriptions, warranties or
        guarantees, and pricing.
      
    `,
  },
  {
    q: 'Why is it important for sellers to have a selling policy?',
    a: `
      
        Having a clear and transparent selling policy helps build trust with
        customers, reduces the likelihood of disputes or misunderstandings, and
        provides a framework for resolving any issues that may arise.
      
    `,
  },
  {
    q: 'What happens if my package gets lost?',
    a: 'If you are the seller and you added insurance to your item before it sold, MX Locker will fully pay you for the cost of your items. If you did not, we can assist you in submitting an insurance claim with the relevant shipping carrier to earn back compensation for the lost package. We cannot guarantee that funds will be rewarded for these claims, and MX Locker is not responsible for compensating sellers when funds for an insurance claim are not rewarded.',
  },
];
export const PAYOUTS_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I payout funds from my MX Locker balance?',
    a: `
      
        You can transfer funds by connecting a debit card or bank account. Once
        connected, use the cash out feature to transfer funds from your MX
        Locker account to any external account.
      
    `,
  },
  {
    q: 'Where is my money held?',
    a: `
      
        All funds from your sales will appear on your MX Locker balance.
      
    `,
  },
  {
    q: 'Can I use the funds from a sale to purchase another item on the platform?',
    a: `
      
        Yes, however the funds on your MX Locker balance must cover the full
        price of the item you are looking to purchase.
      
    `,
  },
  {
    q: 'Why is there a fee attached to my payout?',
    a: `
      
        Your earnings equal the listing price + shipping cost, minus the selling
        fee and payment processing fee. These fees are charged to the Seller
        when the sale has been successfully completed. There is zero charge to
        create a listing. A fee of 10% of the item price for a completed sale.
        Charged to the Seller. MX Locker also collects a 3% payment processing
        fee which is paid out to our payment processor.
      
    `,
  },
  {
    q: 'How long does it take to get paid?',
    a: `
      
        Funds become eligible for payout when the buyer marks the item as received,
        or 48 hours after delivery - whichever comes first. Eligible payouts are sent
        on the next business day, Monday through Friday, excluding bank holidays.
      
    `,
  },
  {
    q: 'Can I instantly transfer my funds once they become available?',
    a: `Yes, however there is a small ‘instant transfer’ fee.`,
  },
  {
    q: 'How do I change my payout account or method?',
    a: `
      
        You are able to add multiple payout accounts or methods when
        transferring your funds on the ‘cash out’ page.
      
    `,
  },
];
export const TRUST_SAFETY_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'How do I know I will get paid after shipping my item?',
    a: `
      
        All funds are held in escrow and are released to the Seller after the
        item is marked as ‘complete’ from the Buyer, or 48 hours after the item
        shows ‘delivered’ - whichever is first. MX Locker will only withhold
        funds if there has been an issue with the order reported within the 48
        hour delivery window.
      
    `,
  },
  {
    q: 'How do I know I will receive what I paid for?',
    a: `
      
        We work hard to build trust within our marketplace. We always suggest
        reviewing a seller’s ratings and communicating with them prior to
        purchasing your item. If you are facing an inappropriate or
        disrespectful seller, please report them to our support team and we will
        remove them from our platform.
      
    `,
  },
  {
    q: `Where can I view a Seller's ratings?`,
    a: `
      
        You are able to view Seller ratings and reviews by visiting their seller
        profile. We allow you to search for a specific Seller, or you can visit
        it from a listing.
      
    `,
  },
  {
    q: 'Why did I receive a warning?',
    a: `
      
        MX Locker may send out warning messages if you have listed a prohibited
        item or engaged in prohibited conduct. Warnings may also include
        temporary suspensions, account restrictions, or termination depending on
        the severity of the warning or number of prior warnings received.
      
    `,
  },
  {
    q: 'Someone is listing a stolen item.',
    a: `
      
        Listing or selling stolen property is strictly forbidden on MX Locker as
        it violates state, federal, and international law.
        <br />
        Stolen property includes: <br />
        - Items taken from private individuals <br />
        - Property taken unlawfully or without authorization from companies or
        governments <br />- Property purchased from someone that was obtained
        unlawfully. This includes items or property which has been sold,
        re-sold, and/or traded where it should have been known by the buyer that
        the item was stolen e.g. well below market value, missing receipts, or
        other indicators
      
    `,
  },
  {
    q: 'How do I report a listing?',
    a: `
      
        We do our best to moderate all listings and remove any prohibited items.
        Slip-ups do happen. If you see something that doesn’t feel right, please
        reach out to our support team.
      
    `,
  },
];
export const GENERAL_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'Is it free to create a seller account on MX Locker?',
    a: `
      
        Yes! Creating a seller on MX Locker is 100% free. A fee is only charged
        once your item sells.
      
    `,
  },
  {
    q: 'How do I create a seller account?',
    a: `
      
        Our seller onboarding process is easy. Complete the required steps,
        which include having a valid email address, mailing address and adding a
        bank account or debit card on file. The card or bank on file is where
        funds will be transferred after an item sells. <strong>Note:</strong>{' '}
        Bank information is encrypted and never stored on MX Locker’ servers.
      
    `,
  },
  {
    q: 'How much profit do I make if a product is sold?',
    a: `
      
        MX Locker has the lowest fees in the industry. Here is a breakdown.
        <br />
        <br />
        <strong>10% seller fee</strong> - This is the standard seller fee charged
        to all sales made through MX Locker. There is no fee for creating a
        listing or if your item does not sell. The 10% seller fee applies to the
        total sale price (including shipping charges) once an item has sold.
        <br />
        <br />
        <strong>3% Payment Processing Fee</strong> - This is the standard
        processing fee banks take to process online transactions. This fee does
        not go to MX Locker.
      
    `,
  },
  {
    q: 'How do I get paid?',
    a: `
      
        Once you have sold an item and it has been delivered to the buyer, head
        over to “Balance” within the seller dashboard. Next click “Withdraw
        money” in the right-hand corner. Choose your payout method and the
        amount you’d like to withdraw.
      
    `,
  },
  {
    q: 'How long does it take to get paid?',
    a: `
      
        After the item has been delivered, buyers have 48 hours to inspect the
        item. After 48 hours or if the buyer marks the item as received, funds will become
        eligible for payout. Eligible payouts are sent on the next business day, Monday
        through Friday, excluding bank holidays. From your MX Locker balance, you can
        withdraw funds instantly or standard payout (1-3 business days).
    `,
  },
  {
    q: 'As a seller, can I cancel an order?',
    a: `
      
        Yes, sellers can cancel orders within 48 hours of purchase. Note: you
        will be charged the credit card processing fee of 3% if you cancel an
        order.
      
    `,
  },
  {
    q: 'How do you avoid scammers?',
    a: `
      
        MX Locker is 100% committed to ensuring that every sale is completed
        with satisfaction to both buyer and seller. Our staff of creators is on
        hand to mediate in the unlikely event a dispute occurs between a buyer
        and seller. Funds are not released to the seller until a package has
        been delivered, allowing MX Locker to work closely to resolve any
        dispute.
        <br />
        <br />
        Because our fraud detection system is robust and our marketplace
        listings team reviews all listings for quality, disputes rarely occur.
      
    `,
  },
  {
    q: 'What happens if the seller never ships my item?',
    a: `
      
        MX Locker holds your funds until the gear is delivered. If you don’t
        receive an item and no tracking is provided within 5 business days from
        purchase you will be issued a full refund.
      
    `,
  },
  {
    q: 'What if an item is damaged in shipping?',
    a: `
      
        If an item is less than $100 dollars it is automatically insured by the
        mail courier. For sellers to be protected, they need to purchase
        shipping protection directly through MX Locker when purchasing a
        shipping label. If an item gets damaged in transit it is 100% insured.
        If shipping insurance is not purchased the seller is responsible for
        refunding the buyer.
      
    `,
  },
  {
    q: 'Are you required to make a purchase if your offer is accepted?',
    a: `
      
        You are responsible for reading the full Item listing before making an
        offer or purchase. When you make an offer and your offer is accepted by
        the Seller, you have a binding contract with the Seller.
        <br />
        <br />A sale transaction is binding on both Seller and Buyer, and no
        returns, refunds, cancellations or retractions are allowed, except as
        permitted by these Terms. Once the offer is accepted by Seller, payment
        is completed instantly and the sale is made. As a buyer, your payment is
        only made if and when the seller accepts - until then no money is taken
        from your account.
      
    `,
  },
  {
    q: 'Does MX Locker offer warranties?',
    a: `
      
        MX Locker does not currently offer any explicit or implied warranties,
        nor any third-party warranties.
      
    `,
  },
  {
    q: 'Does MX Locker facilitate trade internationally?',
    a: `
      
        Currently, MX Locker is open to the following countries: United States,
        Canada, UK, Australia, Switzerland, Italy, Mexico, Japan, China, France,
        Puerto Rico, Ireland. Sellers can choose which countries they are
        willing to ship their listings.
      
    `,
  },
  {
    q: 'Who pays import duties on International items?',
    a: `
      
        If your shipping address is located outside the seller&apos;s country,
        you will be subject to import duties and taxes. The costs of duties are
        NOT included on your MX Locker order and will be billed by the
        transporter during the customs process or upon delivery. NOTE: import
        duties and taxes are non-refundable.
      
    `,
  },
  {
    q: 'How Do Auctions Work?',
    a: `
      
        When auctions on listings are available simply place your bid by
        clicking “place bid.” You&apos;ll place a bid and a max bid. You&apos;ll
        be required to enter payment source and your address.{' '}
        <strong>BIDS ARE FINAL!</strong> This means if you win the bid your
        payment source will be automatically charged.
      
    `,
  },
];

export const FAQ_TOPIC_QUESTION_MAP: Record<FAQTopic, FAQItem[]> = {
  Account: ACCOUNT_FAQ_ITEMS,
  Listing: LISTING_FAQ_ITEMS,
  Purchases: PURCHASE_FAQ_ITEMS,
  'Cancellations & Refunds': CANCELLATION_REFUND_FAQ_ITEMS,
  Shipping: SHIPPING_FAQ_ITEMS,
  Returns: RETURNS_FAQ_ITEMS,
  Selling: SELLING_FAQ_ITEMS,
  Payouts: PAYOUTS_FAQ_ITEMS,
  'Trust & Safety': TRUST_SAFETY_FAQ_ITEMS,
};

export const FAQ_QUESTIONS = Object.values(FAQ_TOPIC_QUESTION_MAP)
  .flat()
  .map((faq) => faq.q);

export const QUESTION_TOPIC_MAP = Object.entries(FAQ_TOPIC_QUESTION_MAP).reduce(
  (acc, entry) => {
    const [topic, faqItems] = entry;
    const questions = faqItems.map((faqItem) => faqItem.q);
    questions.forEach((question) => {
      acc[question] = topic as FAQTopic;
    });
    return acc;
  },
  {} as Record<string, FAQTopic>
);
